import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Paragraph } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { headlineSmall } from '@/styles/typography';

import type { SanityStatsType } from '@/types/sanity';

export interface StatsProps extends SanityStatsType {
  hasLowerDivider?: boolean;
}

const LegacyStats = ({ statistics }: StatsProps) => {
  return (
    <StatisticsContainer>
      {statistics.map((s) => (
        <Statistic key={s._key}>
          <StatFigure>{s.figure}</StatFigure>
          <Paragraph size="body-sm">{s.label}</Paragraph>
        </Statistic>
      ))}
    </StatisticsContainer>
  );
};

const StatsV2 = ({ statistics }: StatsProps) => {
  return (
    <>
      <StatisticsV2Container>
        {statistics.map((s) => (
          <StatisticV2 key={s._key}>
            <StatV2Figure size="headline-md">{s.figure}</StatV2Figure>
            <Paragraph size="body-sm">{s.label}</Paragraph>
          </StatisticV2>
        ))}
      </StatisticsV2Container>
    </>
  );
};

export const Stats = ({
  statsVersion = 'stats-legacy',
  hasLowerDivider,
  statistics,
}: StatsProps) => {
  return (
    <Section verticalPadding>
      {statsVersion === 'stats-v2' ? (
        <StatsV2
          statistics={statistics}
          hasLowerDivider={hasLowerDivider}
          _type="stats"
        />
      ) : (
        <LegacyStats statistics={statistics} _type="stats" />
      )}
    </Section>
  );
};

/*
A version of Stats that is not wrapped in a LayoutWrapper
for use as a component
*/
interface StatsComponentProps
  extends Pick<StatsProps, 'statistics' | 'hasLowerDivider'> {
  className?: string;
  alignLeft?: boolean;
}

export const StatsComponent = ({
  statistics,
  className,
  alignLeft = false,
}: StatsComponentProps) => {
  return (
    <div className={className}>
      <StatisticsV2Container>
        {statistics.map((s) => (
          <StatisticV2 key={s._key} alignLeft={alignLeft}>
            <StatV2Figure size="headline-md">{s.figure}</StatV2Figure>
            <Paragraph size="body-sm">{s.label}</Paragraph>
          </StatisticV2>
        ))}
      </StatisticsV2Container>
    </div>
  );
};

const StatisticsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  margin: var(--spacing-small) 0 var(--spacing-300) 0;
`;

const Statistic = styled.div`
  display: none;
  flex-direction: column;
  text-align: center;
  max-width: 160px;

  p:nth-of-type(2) {
    max-width: 160px;
  }

  ${screen.md} {
    display: block;
  }
`;

const StatFigure = styled(Paragraph)`
  ${headlineSmall};
  color: var(--text-action);
`;

const StatisticsV2Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  gap: var(--spacing-small);

  > div:first-of-type {
    width: 100%;
  }

  > div:not(:first-of-type) {
    max-width: calc(50% - 8px);
  }

  ${screen.sm} {
    gap: var(--spacing-250);
    flex-wrap: nowrap;

    > div,
    div:not(:first-of-type) {
      max-width: 400px;
      width: 100%;
    }
  }

  ${screen.md} {
    gap: var(--spacing-400);
  }

  ${screen.lg} {
    gap: var(--spacing-500);
  }
`;

const StatisticV2 = styled.div<{ alignLeft?: boolean }>`
  flex-direction: column;
  text-align: center;

  ${screen.sm} {
    text-align: ${(props) => (props.alignLeft ? 'left' : 'center')};
  }
`;

const StatV2Figure = styled(Paragraph)`
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-action);
  padding-bottom: var(--spacing-xx-small);
`;
